import type { Tel } from '@w/tel'
import { hasUsableWebGpu } from './page-embeddings-browser.client'
import type { PageSearchEmbeddings } from './page-search'
import { withNemotronQuery, type ServerEmbeddings } from './server-embeddings.client'

/** The app context owns one lazy query worker, independent of per-space backfill. */
export function createBrowserSearchEmbeddings(input: {
  tel: Tel
  server?: ServerEmbeddings
  isSupported?: () => Promise<boolean>
  createWorker?: () => Promise<PageSearchEmbeddings & { close(): void }>
}) {
  let worker: Promise<PageSearchEmbeddings & { close(): void }> | undefined
  let closed = false
  async function createWorker() {
    if (!(await (input.isSupported ?? hasUsableWebGpu)()) || closed) throw new Error('Local query embeddings unavailable')
    const model = input.createWorker ? await input.createWorker()
      : (await import('./page-embeddings-worker-client')).createPageEmbeddingsWorkerClient('flashcards-search.content-cache')
    if (closed) { model.close(); throw new Error('Search context closed') }
    return model
  }
  return Object.assign(withNemotronQuery({ async embedQuery(text, tel) {
    if (closed) throw new Error('Search context closed')
    worker ??= createWorker()
    const model = await worker
    return model.embedQuery(text, tel)
  } }, input.server), {
    async close() {
      if (closed) return
      closed = true
      const model = await worker?.catch(() => null)
      model?.close()
    },
  })
}
